import React, { type FC, createContext, useContext, useState } from 'react'
import { createPortal } from 'react-dom'

import { generateId, noop } from 'helpers/utils'
import Snackbar, { type Item } from 'components/Snackbar'
import { type BaseProps } from 'types'

interface State {
  items: Item[]
  add: (message: string, duration?: number, bottomGap?: number) => void
  bottomGap?: number
}

const initialState: State = {
  items: [],
  add: noop,
  bottomGap: 0,
}

export const SnackbarContext = createContext<State>(initialState)

const SnackbarProvider: FC<BaseProps> = ({ children }) => {
  const add = (message: string, duration?: number, bottomGap?: number): string => {
    const uniqueId = generateId()
    const item: Item = {
      id: uniqueId,
      message,
      duration,
      bottomGap,
    }

    setState(prevState => {
      return {
        ...prevState,
        items: [...prevState.items, item],
        bottomGap,
      }
    })

    return uniqueId
  }

  const remove = (id: string): void => {
    setState(prevState => {
      const items = prevState.items.filter(item => item.id !== id)
      return {
        ...prevState,
        items,
      }
    })
  }

  const [state, setState] = useState<State>({
    items: [],
    add,
  })

  return (
    <SnackbarContext.Provider value={state}>
      {children}
      {createPortal(
        <Snackbar items={state.items} dismiss={remove} bottomGap={state.bottomGap} />,
        document.body,
      )}
    </SnackbarContext.Provider>
  )
}

export const useSnackbar = (): State => {
  return useContext(SnackbarContext)
}

export default SnackbarProvider
